import React from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../../hooks/useLanguage';
import { initWebsiteRuntime } from '../scripts/website-runtime';
import { initTesseractLoader } from '../scripts/loader';
import Nav from './Nav';
import SiteFooter from './SiteFooter';

interface WebsiteLayoutProps {
  children: React.ReactNode;
  // Body class applied while this layout is mounted; used by section-scoped CSS
  // overrides (e.g. .missionos, .vmo) shipped in site.css.
  bodyClass?: string;
  footerVariant?: 'default' | 'missionos' | 'vmo' | 'fda';
  hideCta?: boolean;
  hidePartners?: boolean;
  customCta?: React.ReactNode;
  customPartners?: React.ReactNode;
  hideNav?: boolean;
  hideFooter?: boolean;
}

const SITE_CSS_HREF = '/assets/website/site.css';
const SITE_CSS_ID = 'to-site-css';

// Module-scoped mount tracking: keep site.css attached across SPA navigation
// between website pages (each route's <WebsiteLayout> unmounts/mounts in quick
// succession). Strip the stylesheet only when the user has actually left the
// website tree for a moment — never mid-route-change.
let mountedCount = 0;
let pendingDetachTimer: number | null = null;
let cachedSheetLoaded = false;

// Attaches the website stylesheet if not already present, increments the
// mount counter, and returns `true` if the sheet is ready right now (so the
// caller can skip waiting). Otherwise registers `onLoaded` for the `load` (or
// `error`) event.
function attachSiteCss(onLoaded: () => void): boolean {
  if (typeof document === 'undefined') return true;
  mountedCount++;
  if (pendingDetachTimer != null) {
    window.clearTimeout(pendingDetachTimer);
    pendingDetachTimer = null;
  }
  const existing = document.getElementById(SITE_CSS_ID) as HTMLLinkElement | null;
  if (existing) {
    if (existing.sheet || cachedSheetLoaded) return true;
    existing.addEventListener('load', onLoaded, { once: true });
    existing.addEventListener('error', onLoaded, { once: true });
    return false;
  }
  const link = document.createElement('link');
  link.id = SITE_CSS_ID;
  link.rel = 'stylesheet';
  link.href = SITE_CSS_HREF;
  const handle = () => {
    cachedSheetLoaded = true;
    onLoaded();
  };
  link.addEventListener('load', handle, { once: true });
  // Fail-open: if the load event never fires (network error, bad path), don't
  // leave the page invisible forever.
  link.addEventListener('error', handle, { once: true });
  document.head.appendChild(link);
  if (link.sheet) {
    cachedSheetLoaded = true;
    return true;
  }
  return false;
}

// Decrement the mount counter and, if no <WebsiteLayout> remains mounted after
// a short debounce window, strip the stylesheet so app pages aren't affected.
function detachSiteCss(): void {
  if (typeof document === 'undefined') return;
  mountedCount = Math.max(0, mountedCount - 1);
  if (mountedCount > 0) return;
  if (pendingDetachTimer != null) window.clearTimeout(pendingDetachTimer);
  pendingDetachTimer = window.setTimeout(() => {
    pendingDetachTimer = null;
    if (mountedCount > 0) return;
    const link = document.getElementById(SITE_CSS_ID);
    if (link && link.parentNode) link.parentNode.removeChild(link);
    cachedSheetLoaded = false;
  }, 250);
}

const WebsiteLayout: React.FC<WebsiteLayoutProps> = ({
  children,
  bodyClass,
  footerVariant = 'default',
  hideCta = false,
  hidePartners = false,
  customCta,
  customPartners,
  hideNav = false,
  hideFooter = false,
}) => {
  const [language] = useLanguage();
  const location = useLocation();
  const [cssReady, setCssReady] = React.useState<boolean>(false);

  // Attach the stylesheet *before* the browser paints this commit so the
  // load race is as short as possible. useLayoutEffect runs synchronously
  // after DOM mutations but before paint.
  React.useLayoutEffect(() => {
    const alreadyLoaded = attachSiteCss(() => setCssReady(true));
    document.body.classList.add('to-website');
    if (bodyClass) document.body.classList.add(bodyClass);
    if (alreadyLoaded) setCssReady(true);
    return () => {
      document.body.classList.remove('to-website');
      if (bodyClass) document.body.classList.remove(bodyClass);
      detachSiteCss();
    };
  }, [bodyClass]);

  React.useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  // First-visit tesseract loader (once per browser session).
  React.useEffect(() => {
    initTesseractLoader();
  }, []);

  // Re-bind reveal-on-scroll / nav-strip / cite / force-download after each route change.
  React.useEffect(() => {
    // Defer one frame so newly-rendered elements are in the DOM.
    const raf = requestAnimationFrame(() => initWebsiteRuntime());
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  // Scroll to top on route change (default browser behavior is preserved in MPA;
  // SPA needs the explicit reset).
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);

  // While site.css is loading we render the page but hide it, and lay a cream
  // overlay (matching the tesseract loader's palette) on top so the user
  // doesn't see a white unstyled flash. The overlay uses only inline styles so
  // it works before site.css applies. Fades out the moment cssReady flips.
  const gateStyle: React.CSSProperties = cssReady
    ? {}
    : { visibility: 'hidden' };

  return (
    <>
      <div style={gateStyle}>
        {!hideNav && <Nav />}
        {children}
        {!hideFooter && (
          <SiteFooter
            variant={footerVariant}
            hideCta={hideCta}
            hidePartners={hidePartners}
            customCta={customCta}
            customPartners={customPartners}
          />
        )}
      </div>
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 9998,
          background: '#F0EBE3',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 18,
          fontFamily: 'Urbanist, Inter, system-ui, sans-serif',
          opacity: cssReady ? 0 : 1,
          visibility: cssReady ? 'hidden' : 'visible',
          transition:
            'opacity .35s cubic-bezier(.4,0,.2,1), visibility 0s linear .35s',
          pointerEvents: cssReady ? 'none' : 'auto',
        }}
      >
        <p
          style={{
            fontWeight: 500,
            fontSize: '1.0625rem',
            color: '#1A1F1B',
            margin: 0,
            letterSpacing: '-0.01em',
          }}
        >
          Thoughtful Oasis
        </p>
        <span style={{ width: 36, height: 1, background: '#2DA88D' }} />
      </div>
    </>
  );
};

export default WebsiteLayout;
